// Live per-player news for whatever is selected in the pool.
//
// Called exactly once, in DraftRoom, with the result passed down as props: both
// dossier containers mount at all times and CSS decides which is visible, so a
// hook inside the shared body would fire two identical requests per selection.
// It lives here rather than in DraftProvider because that provider is also
// mounted by the TV board, which must never make this call.
//
// There is no error string in or out of this hook. News degrades the way bye
// weeks do — the section renders as absent, not as an error.

import { useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'
import { normalizeNews } from '../lib/dossier'
import type { NewsItem, PlayerNewsResponse } from '../types/protocol'

export interface PlayerNews {
  items: NewsItem[]
  loading: boolean
  unavailable: boolean
}

const IDLE: PlayerNews = { items: [], loading: false, unavailable: false }

const DEBOUNCE_MS = 200

export function usePlayerNews(code: string, playerId: number | null): PlayerNews {
  const [state, setState] = useState<PlayerNews>(IDLE)
  // Session-scoped and success-only. Caching a failure would pin it for the
  // whole night and defeat the server's 60-second breaker recovery: house wifi
  // drops at 6:50, an owner browses 25 players, wifi returns at 6:52, and all
  // 25 stay newsless because the client never asks again.
  const cacheRef = useRef(new Map<number, PlayerNews>())

  useEffect(() => {
    if (playerId == null) {
      setState(IDLE)
      return
    }

    const cached = cacheRef.current.get(playerId)
    if (cached) {
      setState(cached)
      return
    }

    const controller = new AbortController()
    // Arrow keys walk the pool faster than Sleeper answers. One request for the
    // row you land on, not one per row you pass over — each of those is a cold
    // sleeper_id upstream and one of only three Puma threads.
    //
    // setState({ loading: true }) sits *inside* the timeout on purpose: outside
    // it, flicking past rows would flash "Checking for news…" on every one.
    const timer = setTimeout(() => {
      setState({ items: [], loading: true, unavailable: false })
      // No token: this read is public within the draft, exactly like the pool.
      api<PlayerNewsResponse>(`/drafts/${code}/players/${playerId}/news`, {
        signal: controller.signal,
      })
        .then((payload) => {
          const { items, unavailable } = normalizeNews(payload)
          const next = { items, loading: false, unavailable }
          // Only a real answer is worth remembering.
          if (!unavailable) cacheRef.current.set(playerId, next)
          setState(next)
        })
        .catch((error: unknown) => {
          // An abort is us moving on, not a failure. Leave the state alone —
          // the effect for the new selection has already taken over.
          if (error instanceof DOMException && error.name === 'AbortError') return
          setState({ items: [], loading: false, unavailable: true })
        })
    }, DEBOUNCE_MS)

    // A real AbortController rather than a `cancelled` flag: a boolean
    // suppresses setState but leaves the request in flight, which at OS key
    // repeat is one uncancellable GET per row across three threads.
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [code, playerId])

  return state
}
